/**
 * H5 单栏投影：渲染页面栈栈顶一页。返回三条路径（浏览器返回键 / 应用内按钮 /
 * 右滑手势）统一走 router.back() 出栈，出栈在 popstate 回调完成。
 */
import { topRoute } from '~/nav/projection';
import type { AppRoute } from '~/nav/routes';
import { appRouter } from '~/nav/appRouter';
import { unreachable } from '~/util/invariant';
import { useGlobal } from '~/store/useGlobal';
import { useSwipeBack } from './useSwipeBack';
import styles from './shell.module.scss';
import { ChatPage, HomePage } from './pages';
import { LoginPage } from './LoginPage';
import { SetPasswordPage } from './SetPasswordPage';
import { VlistDemoLazy } from '~/list/demo/VlistDemoLazy';
import { StickerPacksLazy } from './StickerPacksLazy';
import { ContactSearchLazy } from './ContactSearchLazy';
import { ContactApplyLazy } from './ContactApplyLazy';
import { FriendDetailLazy } from './FriendDetailLazy';
import { FriendGroupsLazy } from './FriendGroupsLazy';
import { BlacklistLazy } from './BlacklistLazy';
import { AnnouncementsLazy } from './AnnouncementsLazy';
import { CreateGroupLazy } from './CreateGroupLazy';
import { GroupSettingsLazy } from './GroupSettingsLazy';
import { GroupManageLazy } from './GroupManageLazy';
import { GroupMembersLazy } from './GroupMembersLazy';
import { GroupInviteLazy } from './GroupInviteLazy';
import { GroupLogLazy } from './GroupLogLazy';
import { GroupNoticeLazy } from './GroupNoticeLazy';
import { MyProfileLazy } from './MyProfileLazy';
import { MyPrivacyLazy } from './MyPrivacyLazy';
import { RouteSettingsLazy } from './RouteSettingsLazy';
import { MyAccountsLazy } from './MyAccountsLazy';
import { LoginDevicesLazy, TwoFaLazy } from './AccountSecurityLazy';
import { AboutLazy } from './AboutLazy';
import { ArchiveLazy } from './ArchiveLazy';
import { InstallLazy } from './InstallLazy';
import { FeedbackLazy } from './FeedbackLazy';
import { FeedbackHistoryLazy } from './FeedbackHistoryLazy';
import { AddAccountPage } from './AddAccountPage';
import { MyAppearanceLazy } from './MyAppearanceLazy';
import { QrCodeLazy } from './QrCodeLazy';

export function MobileShell() {
  const nav = useGlobal((s) => s.nav);
  const canGoBack = nav.stack.length > 1;
  const swipeRef = useSwipeBack(canGoBack, () => appRouter().back());
  return (
    <div ref={swipeRef} className={styles.swipeArea} data-testid="swipe-area">
      <TopPage top={topRoute(nav)} />
    </div>
  );
}

function TopPage({ top }: { top: AppRoute }) {
  switch (top.kind) {
    case 'home':
      return <HomePage tab={top.tab} />;
    case 'chat':
      return <ChatPage conversationKey={top.conversationKey} />;
    case 'login':
      return <LoginPage />;
    case 'set-password':
      return <SetPasswordPage />;
    case 'dev-vlist':
      return <VlistDemoLazy />;
    case 'sticker-packs':
      return <StickerPacksLazy />;
    case 'sticker-pack-edit':
      return <StickerPacksLazy packId={top.packId} />;
    case 'contact-search':
      return <ContactSearchLazy />;
    case 'contact-applies':
      return <ContactApplyLazy kind={top.applyKind} />;
    case 'friend-detail':
      return <FriendDetailLazy friendId={top.friendId} />;
    case 'friend-groups':
      return <FriendGroupsLazy />;
    case 'blacklist':
      return <BlacklistLazy />;
    case 'announcements':
      return <AnnouncementsLazy />;
    case 'create-group':
      return <CreateGroupLazy />;
    case 'group-settings':
      return <GroupSettingsLazy groupId={top.groupId} />;
    case 'group-manage':
      return <GroupManageLazy groupId={top.groupId} />;
    case 'group-members':
      return <GroupMembersLazy groupId={top.groupId} />;
    case 'group-invite':
      return <GroupInviteLazy groupId={top.groupId} />;
    case 'group-log':
      return <GroupLogLazy groupId={top.groupId} />;
    case 'group-notice':
      return <GroupNoticeLazy groupId={top.groupId} />;
    case 'me-profile':
      return <MyProfileLazy />;
    case 'me-privacy':
      return <MyPrivacyLazy />;
    case 'me-appearance':
      return <MyAppearanceLazy />;
    case 'me-accounts':
      return <MyAccountsLazy />;
    case 'add-account':
      return <AddAccountPage />;
    case 'me-devices':
      return <LoginDevicesLazy />;
    case 'me-2fa':
      return <TwoFaLazy />;
    case 'me-route':
      return <RouteSettingsLazy />;
    case 'me-about':
      return <AboutLazy />;
    case 'me-feedback':
      return <FeedbackLazy {...(top.target ? { target: top.target } : {})} />;
    case 'me-feedback-history':
      return <FeedbackHistoryLazy />;
    case 'archive':
      return <ArchiveLazy />;
    case 'install':
      return <InstallLazy />;
    case 'me-qrcode':
      return <QrCodeLazy />;
    case 'group-qrcode':
      return <QrCodeLazy groupId={top.groupId} />;
    default:
      return unreachable(top);
  }
}
